import { useEffect, useState } from "react";
import axiosInstance from "../config/axiosInstance";
import { useCompany } from "../hooks/useCompany";
import EmptyState from "../components/EmptyState";
import NoData from "../components/NoData";
import { Link, useNavigate } from "react-router-dom";
import { FiBox, FiEdit2, FiTrash2, FiDownload } from "react-icons/fi";
import { extractList } from "../utils/api";
import Pagination from "../components/Pagination";
import ResponsiveModal from "../components/ResponsiveModal";
import CompanyDraftNotice from "../components/CompanyDraftNotice";
import { downloadSpreadsheet } from "../utils/download";
import { formatDate, statusLabel, statusTone } from "../utils/format";

const SEEN_REJECTIONS_KEY = "shopalli.produtos.reprovacoes-lidas";

const ProductsPage = () => {
  const navigate = useNavigate();
  const { company } = useCompany();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("TODAS");
  const [urgencyFilter, setUrgencyFilter] = useState("TODOS");
  const [activeProductId, setActiveProductId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [rejectedNotice, setRejectedNotice] = useState([]);
  const [exporting, setExporting] = useState(false);
  const itemsPerPage = 20;

  const verificationStatus = company?.statusVerificacao;
  // O cadastro de produtos fica liberado em qualquer status da empresa. Quem
  // decide a vitrine e a aprovacao do proprio produto, feita pela equipe.
  const isPendingApproval = ["PENDENTE", "REJEITADO"].includes(
    verificationStatus,
  );

  // Guarda quais reprovacoes o anunciante ja leu para o aviso nao reaparecer
  // toda vez que ele abre a lista. A chave muda junto com revisadoEm, entao
  // uma nova reprovacao do mesmo produto volta a avisar.
  const readSeenRejections = () => {
    try {
      const stored = window.localStorage.getItem(SEEN_REJECTIONS_KEY);
      const parsed = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  };

  const rejectionKey = (product) =>
    `${product.id}:${product?.revisadoEm || product?.updatedAt || ""}`;

  const markRejectionsAsSeen = (rejectedProducts) => {
    try {
      const merged = Array.from(
        new Set([...readSeenRejections(), ...rejectedProducts.map(rejectionKey)]),
      ).slice(-200);
      window.localStorage.setItem(SEEN_REJECTIONS_KEY, JSON.stringify(merged));
    } catch (error) {
      // localStorage indisponivel: o aviso simplesmente reaparece na proxima visita.
    }
  };

  const fetchProducts = async (page = 1) => {
    if (!company?.id) return;
    setLoading(true);
    try {
      const response = await axiosInstance.get("/products", {
        params: { itemsPerPage, page, companyId: company.id },
      });
      const list = extractList(response);
      setProducts(list);
      setTotalPages(response.data?.data?.totalPages || 1);

      const seen = readSeenRejections();
      const unseenRejections = list.filter(
        (item) =>
          item?.statusAprovacao === "REJEITADO" &&
          !seen.includes(rejectionKey(item)),
      );
      if (unseenRejections.length > 0) {
        setRejectedNotice(unseenRejections);
      }
    } catch (error) {
      setProducts([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(currentPage);
  }, [company?.id, currentPage]);

  const resolveMediaSrc = (mediaItem) => {
    if (!mediaItem || typeof mediaItem !== "string") return "";

    if (
      mediaItem.startsWith("data:") ||
      mediaItem.startsWith("blob:") ||
      mediaItem.startsWith("http://") ||
      mediaItem.startsWith("https://")
    ) {
      return mediaItem;
    }

    return `https://relinq.s3.sa-east-1.amazonaws.com/${mediaItem}`;
  };

  const getProductPreviewSrc = (product) => {
    if (Array.isArray(product?.mediaUrls) && product.mediaUrls[0]) {
      return product.mediaUrls[0];
    }

    if (Array.isArray(product?.media) && product.media[0]) {
      return resolveMediaSrc(product.media[0]);
    }

    return "";
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadSpreadsheet("/products/export", {}, "meus-produtos");
    } catch (error) {
      window.alert("Não foi possível exportar seus produtos. Tente novamente.");
    } finally {
      setExporting(false);
    }
  };

  const closeRejectedNotice = () => {
    markRejectionsAsSeen(rejectedNotice);
    setRejectedNotice([]);
  };

  const handleEditProduct = (productId) => {
    navigate(`/produtos/${productId}/editar`);
  };

  const handleDeleteProduct = async (productId) => {
    const confirmed = window.confirm(
      "Deseja realmente excluir este produto? Esta ação não pode ser desfeita.",
    );

    if (!confirmed) return;

    setActiveProductId(productId);

    try {
      await axiosInstance.delete(`/products/${productId}`);
      setProducts((currentProducts) =>
        currentProducts.filter((product) => product.id !== productId),
      );
    } catch (error) {
      window.alert("Não foi possível excluir o produto. Tente novamente.");
    } finally {
      setActiveProductId(null);
    }
  };

  const categories = Array.from(
    new Set(
      products
        .map(
          (product) =>
            product?.Category?.name ||
            product?.category?.name ||
            product?.categoryName,
        )
        .filter(Boolean),
    ),
  ).sort((a, b) => a.localeCompare(b));

  const filteredProducts = products.filter((product) => {
    const title = product?.title || "";
    const categoryName =
      product?.Category?.name ||
      product?.category?.name ||
      product?.categoryName ||
      "";
    const condition = product?.priceCondition || "";

    const matchesSearch =
      !searchTerm ||
      [title, categoryName, condition]
        .join(" ")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesCategory =
      categoryFilter === "TODAS" || categoryName === categoryFilter;

    const matchesUrgency =
      urgencyFilter === "TODOS" ||
      (urgencyFilter === "COM_URGENCIA" && product?.hasUrgency) ||
      (urgencyFilter === "SEM_URGENCIA" && !product?.hasUrgency);

    return matchesSearch && matchesCategory && matchesUrgency;
  });

  // if (!company?.id) {
  //   return (
  //     <EmptyState
  //       title="Finalize seu cadastro"
  //       description="Complete os dados da empresa para cadastrar e gerenciar produtos."
  //       actionLabel="Finalizar cadastro"
  //       onAction={() => window.location.assign("/empresa")}
  //     />
  //   );
  // }

  // Paginação

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  return (
    <div className="fade-in page-stack">
      <CompanyDraftNotice company={company} />

      <div className="page-header">
        <div>
          <h2>Produtos</h2>
          <p>Lista de produtos cadastrados pela sua empresa.</p>
          {isPendingApproval ? (
            <p className="muted" style={{ marginTop: "0.35rem" }}>
              Você já pode cadastrar e anunciar seus produtos. Cada item passa por
              uma revisão da nossa equipe e vai para a vitrine assim que for
              aprovado — sem precisar esperar a verificação da empresa.
            </p>
          ) : null}
        </div>
        <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
          <button
            type="button"
            className="button button--ghost"
            onClick={handleExport}
            disabled={exporting}
            style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
          >
            <FiDownload size={16} />
            {exporting ? "Gerando Excel..." : "Exportar Excel"}
          </button>
          <Link className="button button--primary" to="/produtos/novo">
            Novo Produto
          </Link>
        </div>
      </div>

      <div className="section-card">
        <h3 className="card-title">Produtos cadastrados</h3>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "12px",
            alignItems: "end",
            marginBottom: "16px",
          }}
        >
          <div className="field" style={{ margin: 0 }}>
            <label className="label">Buscar produto</label>
            <input
              className="input"
              type="text"
              placeholder="Nome, categoria ou condição"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>

          <div className="field" style={{ margin: 0 }}>
            <label className="label">Categoria</label>
            <select
              className="select"
              value={categoryFilter}
              onChange={(event) => setCategoryFilter(event.target.value)}
            >
              <option value="TODAS">Todas</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div className="field" style={{ margin: 0 }}>
            <label className="label">Urgência</label>
            <select
              className="select"
              value={urgencyFilter}
              onChange={(event) => setUrgencyFilter(event.target.value)}
            >
              <option value="TODOS">Todos</option>
              <option value="COM_URGENCIA">Com alerta</option>
              <option value="SEM_URGENCIA">Sem alerta</option>
            </select>
          </div>

          <button
            className="button button--ghost"
            type="button"
            onClick={() => {
              setSearchTerm("");
              setCategoryFilter("TODAS");
              setUrgencyFilter("TODOS");
            }}
            style={{ height: "44px", width: "100%" }}
          >
            Limpar
          </button>
        </div>

        {loading ? (
          <div className="muted">Carregando produtos...</div>
        ) : products.length === 0 ? (
          <NoData
            title="Nenhum produto cadastrado"
            description={
              isPendingApproval
                ? "Você ainda não cadastrou produtos. Comece agora — cada item vai para a vitrine assim que a equipe aprovar."
                : "Você ainda não cadastrou produtos. Adicione seu primeiro item ao seu catálogo."
            }
          >
            <button
              className="button button--primary"
              style={{ marginTop: "1.2rem" }}
              onClick={() => navigate("/produtos/novo")}
            >
              Adicionar Produto
            </button>
          </NoData>
        ) : filteredProducts.length === 0 ? (
          <NoData title="Nenhum resultado para os filtros" description="Tente ajustar os filtros para encontrar produtos." />
        ) : (
          <>
          <table className="table">
            <thead>
              <tr>
                <th>Produto / Lote</th>
                <th>Categoria</th>
                <th>Condição</th>
                <th>Validade Próxima</th>
                <th>Status</th>
                <th>Atualizado</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => {
                const categoryName =
                  product?.Category?.name ||
                  product?.category?.name ||
                  product?.categoryName ||
                  "-";
                const price = product?.priceCondition || "-";
                const urgencyBadge = product?.hasUrgency ? (
                  <span
                    style={{
                      backgroundColor: "#ffedd5",
                      color: "#ea580c",
                      padding: "0.25rem 0.5rem",
                      borderRadius: "9999px",
                      fontSize: "0.85rem",
                      fontWeight: "600",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Laranja Alerta
                  </span>
                ) : (
                  <span className="muted">-</span>
                );
                const updatedAt = formatDate(
                  product?.updatedAt || product?.createdAt,
                );
                const approvalStatus = product?.statusAprovacao || "PENDENTE";

                return (
                  <tr key={product.id}>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.75rem",
                        }}
                      >
                        {getProductPreviewSrc(product) && (
                          <img
                            src={getProductPreviewSrc(product)}
                            alt={product.title}
                            style={{
                              width: "40px",
                              height: "40px",
                              objectFit: "cover",
                              borderRadius: "0.25rem",
                              border: "1px solid var(--color-border)",
                            }}
                          />
                        )}
                        <strong>{product?.title || "-"}</strong>
                      </div>
                    </td>
                    <td>{categoryName}</td>
                    <td>{price}</td>
                    <td>{urgencyBadge}</td>
                    <td>
                      <span className="badge" data-tone={statusTone(approvalStatus)}>
                        {approvalStatus === "REJEITADO"
                          ? "Reprovado"
                          : statusLabel(approvalStatus)}
                      </span>
                      {approvalStatus === "REJEITADO" && (
                        <button
                          type="button"
                          onClick={() => setRejectedNotice([product])}
                          style={{
                            display: "block",
                            marginTop: "0.3rem",
                            padding: 0,
                            border: "none",
                            background: "none",
                            color: "#b91c1c",
                            fontSize: "0.76rem",
                            fontWeight: 600,
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                        >
                          Ver motivo
                        </button>
                      )}
                    </td>
                    <td>{updatedAt}</td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <button
                          className="button button--ghost"
                          type="button"
                          onClick={() => handleEditProduct(product.id)}
                          aria-label={`Editar produto ${product?.title || ""}`}
                          title="Editar produto"
                          style={{
                            width: "40px",
                            height: "40px",
                            padding: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <FiEdit2 size={16} />
                        </button>
                        <button
                          className="button button--ghost"
                          type="button"
                          onClick={() => handleDeleteProduct(product.id)}
                          aria-label={`Excluir produto ${product?.title || ""}`}
                          title="Excluir produto"
                          disabled={activeProductId === product.id}
                          style={{
                            width: "40px",
                            height: "40px",
                            padding: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#b91c1c",
                          }}
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* Paginação bonita */}
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
          </>
        )}
      </div>

      <ResponsiveModal
        isOpen={rejectedNotice.length > 0}
        onClose={closeRejectedNotice}
        title={
          rejectedNotice.length > 1
            ? `${rejectedNotice.length} produtos reprovados`
            : "Produto reprovado"
        }
        subtitle="revisão"
        size="md"
        primaryAction={{ label: "Entendi", onClick: closeRejectedNotice }}
      >
        <p style={{ marginTop: 0, color: "#475569", fontSize: "0.92rem" }}>
          Nossa equipe revisou seu catálogo e reprovou{" "}
          {rejectedNotice.length > 1 ? "os itens abaixo" : "o item abaixo"}. Corrija o que
          foi apontado e salve o produto para ele voltar para a fila de aprovação.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          {rejectedNotice.map((product) => (
            <div
              key={product.id}
              style={{
                border: "1px solid #fecaca",
                backgroundColor: "#fef2f2",
                borderRadius: "0.6rem",
                padding: "0.85rem",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "0.7rem" }}>
                {getProductPreviewSrc(product) && (
                  <img
                    src={getProductPreviewSrc(product)}
                    alt={product?.title || "Produto"}
                    style={{
                      width: "46px",
                      height: "46px",
                      objectFit: "cover",
                      borderRadius: "0.35rem",
                      border: "1px solid #fecaca",
                    }}
                  />
                )}
                <strong style={{ color: "#7f1d1d" }}>{product?.title || "Produto"}</strong>
              </div>

              <p style={{ margin: "0.6rem 0 0", color: "#991b1b", fontSize: "0.88rem" }}>
                <strong>Motivo: </strong>
                {product?.motivoRejeicao || "Motivo não informado pela equipe de revisão."}
              </p>

              {product?.revisadoEm && (
                <p style={{ margin: "0.35rem 0 0", color: "#b91c1c", fontSize: "0.76rem" }}>
                  Revisado em {formatDate(product.revisadoEm)}
                </p>
              )}

              <button
                type="button"
                className="button button--ghost"
                style={{ marginTop: "0.7rem", padding: "0.4rem 0.8rem", fontSize: "0.82rem" }}
                onClick={() => {
                  closeRejectedNotice();
                  handleEditProduct(product.id);
                }}
              >
                Corrigir produto
              </button>
            </div>
          ))}
        </div>
      </ResponsiveModal>
    </div>
  );
};

export default ProductsPage;
